import { type JSX, useState } from "react";
import {
  Box,
  BoxProps,
  ClickAwayListener,
  ThemeProvider,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tooltipClasses,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAppTheme } from "@/theme";

type ExtendedProps = TooltipProps & {
  disableMaxWidth: boolean;
  tooltipMaxWidth?: number | string;
};

interface RichTooltipProps {
  children: JSX.Element;
  title?: React.ReactNode;
  description?: string;
  body?: JSX.Element;
  // These are negative checks because we prefer these to be on by default
  disableTooltip?: boolean;
  disableMaxWidth?: boolean;
  compact?: boolean;
  placement?: TooltipProps["placement"];
  tooltipMaxWidth?: number | string;
  containerProps?: BoxProps;
  targetProps?: BoxProps;
  tooltipProps?: Omit<
    ExtendedProps,
    "title" | "className" | "disableMaxWidth" | "tooltipMaxWidth" | "children"
  >;
}

const StyledContainer = styled(Box)(() => ({}));
const TargetTooltip = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
  {
    shouldForwardProp: (prop) =>
      prop !== "disableMaxWidth" && prop !== "tooltipMaxWidth",
  }
)<ExtendedProps>(({ theme, tooltipMaxWidth }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.grey[200],
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.grey[200],
    boxShadow: "18px 0px 28px 2px rgba(0, 0, 0, 0.2)",
    borderRadius: 4,
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: theme.palette.grey[400],
    maxWidth: "300px",
    color: "white",
    padding: 0,
    ...(tooltipMaxWidth ? { maxWidth: tooltipMaxWidth } : {}),
  },
  variants: [
    {
      props: ({ disableMaxWidth }) => disableMaxWidth,
      style: {
        [`& .${tooltipClasses.tooltip}`]: {
          maxWidth: "none",
        },
      },
    },
  ],
}));

export default function RichTooltip({
  title,
  description,
  body,
  disableMaxWidth = false,
  disableTooltip = false,
  compact = false,
  tooltipProps = {},
  children,
  containerProps,
  targetProps,
  placement,
  tooltipMaxWidth,
}: RichTooltipProps) {
  const currentTheme = useTheme();

  const [open, setOpen] = useState(false);
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setOpen(!open);
  };
  /**
   * Currently RichTooltip has only dark mode, so we need to wrap
   * the tooltip in dark theme, to allow developing theme children
   */
  const theme = useAppTheme("dark");
  const isTouchDevice = useMediaQuery("(hover: none) and (pointer: coarse)");
  const hasTooltipContent = title || description || body;
  if (disableTooltip || !hasTooltipContent) return children;

  const tooltipTitle = (
    <StyledContainer
      sx={[
        compact
          ? {
              p: 2,
            }
          : {
              p: 3,
            },
      ]}
    >
      {title ? (
        <>
          {typeof title === "string" ? (
            <Typography variant="body2" color="text.primary">
              {title}
            </Typography>
          ) : (
            title
          )}
        </>
      ) : null}
      {description ? (
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            ...(description &&
              title && {
                borderTop: 1,
                borderColor: "divider",
                mt: 1,
                pt: 1,
              }),
          }}
        >
          {description}
        </Typography>
      ) : null}
      {body}
    </StyledContainer>
  );

  return (
    <ThemeProvider theme={theme}>
      {isTouchDevice ? (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box component="span" {...containerProps}>
            <TargetTooltip
              key="touch"
              onClick={handleClick}
              disableMaxWidth={disableMaxWidth}
              tooltipMaxWidth={tooltipMaxWidth}
              arrow
              placement={placement}
              title={tooltipTitle}
              {...tooltipProps}
              open={open}
              onClose={() => setOpen(false)}
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <Box component="span" {...targetProps}>
                <ThemeProvider theme={currentTheme}>{children}</ThemeProvider>
              </Box>
            </TargetTooltip>
          </Box>
        </ClickAwayListener>
      ) : (
        <TargetTooltip
          key="non-touch"
          disableMaxWidth={disableMaxWidth}
          tooltipMaxWidth={tooltipMaxWidth}
          arrow
          placement={placement}
          title={tooltipTitle}
          {...tooltipProps}
        >
          <Box component="span" {...containerProps}>
            <ThemeProvider theme={currentTheme}>{children}</ThemeProvider>
          </Box>
        </TargetTooltip>
      )}
    </ThemeProvider>
  );
}
